/**
 * MathText — renders a string that may contain inline ($...$) or display
 * ($$...$$) LaTeX, using KaTeX. Plain text outside the delimiters is rendered
 * normally (React-escaped). Questions/options without any `$` take a fast path
 * and never touch KaTeX, so existing plain-text content is unaffected.
 */
import React, { useMemo } from "react";
import katex from "katex";
import "katex/dist/katex.min.css";

const splitMath = (text) => {
  const parts = [];
  // $$display$$ first, then $inline$
  const regex = /\$\$([\s\S]+?)\$\$|\$([^$\n]+?)\$/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push({ t: "text", v: text.slice(last, m.index), key: key++ });
    const display = m[1] != null;
    parts.push({ t: "math", v: display ? m[1] : m[2], display, key: key++ });
    last = regex.lastIndex;
  }
  if (last < text.length) parts.push({ t: "text", v: text.slice(last), key: key++ });
  return parts;
};

const MathText = ({ text = "", className, as: Tag = "span" }) => {
  const segments = useMemo(() => {
    if (!text || !text.includes("$")) return null; // fast path: no math
    return splitMath(text);
  }, [text]);

  if (!segments) return <Tag className={className}>{text}</Tag>;

  return (
    <Tag className={className}>
      {segments.map((s) =>
        s.t === "text" ? (
          <React.Fragment key={s.key}>{s.v}</React.Fragment>
        ) : (
          <span
            key={s.key}
            // KaTeX output with throwOnError:false is safe, self-contained HTML.
            dangerouslySetInnerHTML={{
              __html: katex.renderToString(s.v, {
                throwOnError: false,
                displayMode: s.display,
                output: "html",
              }),
            }}
          />
        )
      )}
    </Tag>
  );
};

export default MathText;
